import Backbone, {ModelSetOptions} from 'backbone';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {NucleotideType, ResidueType} from 'biojs/BioConstants';
import {Sequence} from 'components/Sequence';
import {SliceMapType} from 'constants/MapsConstants';
import {OPPOSITE_SIDE, Side} from 'constants/MolbioConstants';
import * as context from 'Context';
import {CopyNucleotideSequenceMode} from 'molbio/CopyUtil';
import CutSite from 'molbio/enzymes/backbone/CutSite';
import * as selectables from 'molbio/maps/selectables/Selectables';
import SequencePrimer from 'molbio/primers/backbone/SequencePrimer';
import Protein from 'molbio/proteins/Protein';
import * as sequtil from 'molbio/SeqUtil';
import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import * as FeaturePanelUIReduxActions from 'toolbar/feature-panel/FeaturePanelUIReduxActions';
import {ClipboardMeta} from 'util/BenchlingClipboard';
import * as logging from 'util/Logging';
import {MakeAllFieldsOptional, Optional} from 'util/Types';

export const NCBI_BLAST_MAX_LENGTH = 5000;
const COPY_MAX_LENGTH = 500000;

interface StartEndStateOptions {
  cutsite?: CutSite;
  primer?: SequencePrimer;
}

type StartOrEndStateKey = keyof StartEndStateOptions;

type Handler = 'reset' | 'circularExtend' | 'linearExtend' | 'setEndpoints' | 'drag';

export interface SelectionAttributes {
  start: number;
  end: null | number;
  active: null | boolean | SliceMapType | 'circular';
  sequence: Sequence | Protein;
  anchor: Side;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handlers: {[handler in Handler]?: Array<(...args: Array<any>) => any>};
  // column could be a top-level attribute. It seems that this state previously held more complex data.
  state: {column?: number};
  startState: StartEndStateOptions;
  endState: StartEndStateOptions;
}

interface Selection {
  get<K extends keyof SelectionAttributes>(key: K): SelectionAttributes[K];
  set(obj: Partial<SelectionAttributes>, options?: ModelSetOptions): Selection;
  set<K extends keyof SelectionAttributes>(
    key: K,
    value: SelectionAttributes[K],
    options?: ModelSetOptions
  ): Selection;
}

interface NonEmptySelectionAttributes extends SelectionAttributes {
  end: number;
}

interface NonEmptySelection extends Selection {
  get<K extends keyof NonEmptySelectionAttributes>(key: K): NonEmptySelectionAttributes[K];
  set(obj: Partial<NonEmptySelectionAttributes>, options?: ModelSetOptions): NonEmptySelection;
  set<K extends keyof NonEmptySelectionAttributes>(
    key: K,
    value: NonEmptySelectionAttributes[K],
    options?: ModelSetOptions
  ): NonEmptySelection;
  end(): number;
}

/**
 * Selection maintains the selection state associated with a polymer for sequences, proteins, and alignments.
 */
class Selection extends Backbone.Model {
  defaults(): MakeAllFieldsOptional<SelectionAttributes> {
    return {
      start: 0,
      end: null,
      active: null, // null, linear, or circular
      sequence: null,
      anchor: 'start',
      state: {},
      startState: {},
      endState: {},
      handlers: {}, // reset: null, extend: null
    };
  }

  allowEdits(): boolean {
    return this.polymer().allowResidueEdits();
  }

  getPolymerLength(): number {
    return this.polymer().length();
  }

  isPolymerCircular(): boolean {
    return this.polymer().circular();
  }

  triggerJump(start: number, end: number): void {
    return this.polymer().trigger('jump', start, end);
  }

  deleteSelection(): void {
    this.polymer().deleteSelection();
  }

  isWritable(): boolean {
    return this.polymer().allowResidueEdits() && this.polymer().isSelectionEditable();
  }

  isDeletable(): boolean {
    return this.polymer().allowResidueEdits() && this.polymer().isSelectionDeletable();
  }

  getSelectionResidues(): string {
    // We can take a slice when the end is null (going all the way to the end) but it is
    // potentially surprising behavior and likely not what the caller wants.
    const nonEmptySelection = this.assertNotEmpty();
    return this.polymer().residueSlice(nonEmptySelection.start(), nonEmptySelection.end());
  }

  initialize(): void {
    // eslint-disable-next-line global-require
    const Protein = require('molbio/proteins/Protein').default;
    // eslint-disable-next-line global-require
    const ProteinActions = require('molbio/proteins/ProteinActions').default;
    // eslint-disable-next-line global-require
    const ProteinStore = require('molbio/proteins/ProteinStore').default;

    // Warn us if selection end is ever 0, so we can fix it.
    this.on('change:end', () => {
      assertAndContinue(this.end() !== 0, 'Somehow, selection end got set to 0.');
    });
    if (this.polymer() instanceof Protein) {
      const proteinId = this.polymer().id;
      this.on('change:start change:end', () => {
        _.defer(() => ProteinActions.selectRange(proteinId, this.start(), this.end()));
      });
      ProteinStore.addChangeListener(() => this.set(ProteinStore.getSelection(proteinId)));
    } else if (this.polymer() != null) {
      this.on('change:start change:end', () => {
        _.defer(() =>
          // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
          getStore().dispatch(
            FeaturePanelUIReduxActions.changeSelectionRegion(this.polymer().id, this.start(), this.end())
          )
        );
      });
    }
  }

  isCopyable(): boolean {
    const selectionLength = this.length();
    return 0 < selectionLength && selectionLength <= COPY_MAX_LENGTH;
  }

  /* Whether the selection meets the criteria to submit to NCBI BLAST (not internal BLAST). */
  isBlastable(): boolean {
    const selectionLength = this.length();
    return 0 < selectionLength && selectionLength <= NCBI_BLAST_MAX_LENGTH;
  }

  isReallyBig(): boolean {
    return this.length() > 400000;
  }

  /* @deprecated Callers should invoke polymer() instead for clarity */
  sequence(): Sequence | Protein {
    return this.get('sequence');
  }

  polymer(): Sequence | Protein {
    return this.get('sequence');
  }

  anchorEnd(): Side {
    return this.get('anchor');
  }

  freeEnd(): Side {
    return OPPOSITE_SIDE[this.get('anchor')];
  }

  start(): number {
    return this.get('start');
  }

  end(): number | null {
    return this.get('end');
  }

  _getModelAttribute(attribute: Side): number | null {
    assert(['start', 'end'].includes(attribute), `Unrecognized selection attribute: ${attribute}`);
    let value = this.get(attribute);
    if (value == null) {
      return value;
    }
    const length = this.getPolymerLength();
    if (length === 0) {
      assert(value === 0, '');
      return value;
    }

    assertAndContinue(value >= 0, `Selection ${attribute} is out of bounds: ${value} >= 0 check failed.`);
    // Because of parts of the sequence may have been deleted after this value was gotten, there is a chance
    // there's a chance that the value is higher than the length.
    if (value > length) {
      logging.warn(
        `Selection is out of bounds: value > length. This could be a result \
of a sequence part being deleted.`,
        {extra: {attribute, value, length}}
      );
      value = length;
    }
    return sequtil.mod(value, length);
  }

  modelStart(): number | null {
    return this._getModelAttribute('start');
  }

  modelEnd(): number | null {
    return this._getModelAttribute('end');
  }

  residueType(): ResidueType {
    return this.polymer() instanceof Protein ? ResidueType.AA : ResidueType.NUCLEOTIDE;
  }

  calcLength(s: number, e: number | null): number {
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    if (e === null) {
      return 0;
    } else if (s === e || (s === 0 && e === this.getPolymerLength())) {
      return this.getPolymerLength();
    } else {
      return (e - s + this.getPolymerLength()) % this.getPolymerLength();
    }
  }

  length(): number {
    return this.calcLength(this.start(), this.end());
  }

  clear(index: unknown): void {
    this.reset(0);
  }

  contains(index: number): boolean {
    if (this.isEmpty()) {
      return false;
    }
    const sStart = this.start();
    const sEnd = this.end()!;

    if (sStart < sEnd) {
      return sStart <= index && index < sEnd;
    } else {
      return index >= sStart || index < sEnd;
    }
  }

  slice(start: number, end: number): [] | [this] {
    const sStart = this.start();
    const sEnd = this.end();
    let result: [] | [this] = [];

    if (this.isEmpty()) {
      if (start <= sStart && sStart < end) {
        result = [this];
      } else if (end === this.getPolymerLength() && sStart === end) {
        result = [this];
      }
    } else if (sStart === sEnd) {
      result = [this];
    } else if ((sStart > sEnd! && (end > sStart || start < sEnd!)) || (sStart < end && sEnd! > start)) {
      result = [this];
    }
    return result;
  }

  isEmpty(): boolean {
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    return this.end() === null;
  }

  isNotEmpty(): this is NonEmptySelection {
    return !this.isEmpty();
  }

  assertNotEmpty(): NonEmptySelection {
    assert(this.isNotEmpty(), 'Expected range to not be empty.');
    return this;
  }

  canDigest(): boolean {
    return this.getStartState('cutsite') != null && this.getEndState('cutsite') != null;
  }

  canPCR(): boolean {
    const startStatePrimer = this.getStartState('primer');
    const endStatePrimer = this.getEndState('primer');
    if (startStatePrimer == null || endStatePrimer == null) {
      return false;
    } else if (
      startStatePrimer.nucleotideType() === NucleotideType.RNA ||
      endStatePrimer.nucleotideType() === NucleotideType.RNA
    ) {
      return false;
    }
    return startStatePrimer.strand() === 1 && endStatePrimer.strand() === -1;
  }

  canInvert(): boolean {
    return this.isPolymerCircular() || this.start() === 0 || this.end() === this.getPolymerLength();
  }

  select(d: selectables.Model, start: number | null = null, end: number | null = null): void {
    if (start == null) {
      start = d.start();
    }
    if (end == null) {
      end = d.end();
    }

    // eslint-disable-next-line global-require
    const GapSequenceWrapper = require('analyses/alignment/GapSequenceWrapper').default;
    if (sequtil.isWrap(start, end) && this.polymer() instanceof GapSequenceWrapper) {
      notifications.warn('Selections around the origin are disabled on alignments.', {
        id: 'circular-alignment-selection',
      });
      return;
    }

    const selection = this;
    selection.reset(d.start());
    const unselect = (d: selectables.Model) =>
      function (selection: Selection): Selection {
        d.set({selected: false});
        return selection.clearHandlers();
      };
    end = end === 0 ? this.getPolymerLength() : end;
    selection.setEndpoints(start, end);
    selection.setFreeEnd(d.strand() === -1 ? 'start' : 'end');
    d.set({selected: true});

    selection.addHandlers({
      reset: unselect(d),
      circularExtend: unselect(d),
      linearExtend: unselect(d),
      setEndpoints: unselect(d),
    });
    this.triggerJump(start, end);
  }

  reset(x?: Optional<number>): void {
    const sequenceLength = this.getPolymerLength();
    const assertion = x != null && !Number.isNaN(x) && 0 <= x && x <= sequenceLength;
    if (!assertion) {
      // If the assertion fails, at least use a reasonable value.
      // Skip logging assertion failure because it does not cause major issues for users
      // and we don't have a good way to address it currently.
      x = 0;
    }

    this.set({start: x!, end: null, active: null, anchor: 'start'});
    this._callHandles('reset', x);
    this.set('state', {});
    this.set('startState', {});
    this.set('endState', {});

    selectables.deselect({event: 'reset'});
  }

  resetBy(delta: number): void {
    let newX = this.start() + delta;
    const length = this.getPolymerLength();

    if ((newX < 0 || newX > length) && !this.isPolymerCircular()) {
      return;
    }

    if (length === 0) {
      this.reset(0);
    } else {
      newX = sequtil.modInclusive(newX, length);
      this.reset(newX);
    }
  }

  setEndpoints(start: number | null, end: number | null, options?: ModelSetOptions): void {
    if (options == null) {
      options = {};
    }
    if (end === 0) {
      end = this.getPolymerLength();
    }
    // If the assertion fails, at least use reasonable values.
    let assertion = start != null && !Number.isNaN(start) && !Number.isNaN(end);
    assertAndContinue(assertion, `Cannot set selection endpoints to: ${start}, ${end}`);
    if (!assertion) {
      start = 0;
      end = null;
    }
    assertion = end == null || start! <= end || this.isPolymerCircular();
    if (!assertion) {
      // Skip logging assertion failure because it does not cause major issues for users
      // and we don't have a good way to address it currently.
      [start, end] = [end, start];
    }

    const length = this.getPolymerLength();
    if (length === 0) {
      this.set({start: 0, end: null, active: null, anchor: 'start'}, options);
    } else {
      this.set({start: start!, end}, options);
    }
  }

  getEndpoints(): [number, number | null] {
    return [this.start(), this.end()];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFreeEnd(type: Side, options?: ModelSetOptions): any {
    if (options == null) {
      options = {};
    }
    this.set('anchor', type, options);
    this.set('anchor', this.freeEnd(), options);
    return this._callHandles('drag');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _callHandles(method: Handler, ...args: Array<any>): Array<any> | undefined {
    if (this.get('handlers')[method] != null) {
      return this.get('handlers')[method]!.map((f) => f(this, ...args));
    }
    // eslint-disable-next-line no-useless-return
    return;
  }

  circularExtendBy(delta: number): void {
    const length = this.getPolymerLength();
    if (length === 0) {
      return;
    }
    let oldX = this.get(this.freeEnd());
    if (oldX == null) {
      oldX = this.get(this.anchorEnd());
    }
    const newX = (oldX! + delta + length) % length;
    this.circularExtend(newX);
  }

  circularExtend(x: number): void {
    const length = this.getPolymerLength();
    if (length === 0) {
      return;
    }

    // We have intermediate changes to selection that we don't want other people to know about,
    // so we change everything silently, then reset the old values, and set the new values without silent:true

    const movedEnd = this.freeEnd();
    const oldX = this.get(this.freeEnd());
    const start = this.start();
    const end = this.end();
    const changedValues = ['start', 'end', 'anchor', 'startState', 'endState'];
    const previousValues = this.pick(...changedValues);
    const options = {silent: true};

    const selectionSize = this.calcLength(start, end);
    const newSelectionSize = this.freeEnd() === 'start' ? this.calcLength(x, end) : this.calcLength(start, x);

    const isNearFull = selectionSize > (3 / 4) * length;
    const bigChange = Math.abs(newSelectionSize - selectionSize) > length / 2;

    if (bigChange) {
      if (!isNearFull) {
        this.set(this.freeEnd(), x, options);
        this._swap(options);

        if (this.start() === this.end()) {
          this.set('end', null, options);
          this.setFreeEnd('end', options);
        }
      } else if (this.freeEnd() === 'end') {
        this.set('end', start, options);
      } else if (this.freeEnd() === 'start') {
        this.set('start', end === length ? 0 : end!, options);
      }
    } else {
      this.set(this.freeEnd(), x, options);
    }

    this._cleanUpStartAndEnd(options);
    const values = this.pick(...changedValues);
    this.set(previousValues, options);
    this.set(values);
    this.unselectHelper(movedEnd);
    this._callHandles('circularExtend', oldX, x, movedEnd);
  }

  _expandHelper(s2: number, e2: number, trigger: boolean): this | void {
    e2 = e2 === 0 ? this.getPolymerLength() : e2;
    const s1 = this.start();
    const e1 = this.end();

    // If either we have a circular selection or the range we want to add is circular, allow circular.
    const allowCircular = sequtil.isWrap(s1, e1) || sequtil.isWrap(s2, e2) || !context.get('isLinear');

    const tryRange = (
      s: number,
      e: number | null,
      includes: Array<number | null>
    ): {
      len: number;
    } | null => {
      // If we are avoiding circular, then dont allow it!
      if (sequtil.isWrap(s, e) && !allowCircular) {
        return null;
      }

      if (s == null || e == null) {
        return null;
      }
      if (sequtil.isWrap(s, e) && !this.isPolymerCircular()) {
        return null;
      }
      includes = includes.filter((item) => item != null);
      if (!sequtil.doesRangeInclude(s, e, includes as Array<number>)) {
        return null;
      }
      return {len: sequtil.length(s, e, this.getPolymerLength())};
    };

    // @ts-expect-error: possibly subtracting from null
    const r1 = tryRange(s1, e2, [s2, e1 - 1]);
    const r2 = tryRange(s2, e1, [s1, e2 - 1]);
    // @ts-expect-error: possibly subtracting from null
    const r3 = tryRange(s2, e2, [s1, e1 - 1]);
    const r4 = tryRange(s1, e1, [s2, e2 - 1]);
    // One cursor cases
    const r5 = e1 == null ? tryRange(s1, e2, [s2]) : undefined;
    const r6 = e1 == null ? tryRange(s2, s1, [e2 - 1]) : undefined;
    const r = _.min(_.compact([r1, r2, r3, r4, r5, r6]), (r) => r.len);
    switch (r) {
      case r1:
        this.forceMoveEnd('end', e2, trigger);
        break;
      case r2:
        this.forceMoveEnd('start', s2, trigger);
        break;
      case r3:
        this.setEndpoints(s2, e2);
        break;
      case r4: // We are good.
        break;
      case r5:
        this.forceMoveEnd('end', e2, trigger);
        break;
      case r6:
        this.forceMoveEnd('start', s2, trigger);
        break;
      default:
        this.setEndpoints(s1, s1);
    }
  }

  expandRange(x: number, y: number, trigger?: boolean): this | void {
    if (trigger == null) {
      trigger = true;
    }
    return this._expandHelper(x, y, trigger);
  }

  _cleanUpStartAndEnd(options?: ModelSetOptions): void {
    // Make sure start and end are properly set to 0 or length.
    const length = this.getPolymerLength();
    if (!this.isEmpty() && this.start() === length) {
      this.set('start', 0, options);
    }
    if (this.end() === 0) {
      this.set('end', length, options);
    }
  }

  forceMoveEnd(moveEnd: Side, pos: number, trigger: boolean, collapse?: boolean): void {
    if (collapse == null) {
      collapse = true;
    }
    const oldPos = this.get(moveEnd);
    const changedValues = ['start', 'end', 'anchor', 'startState', 'endState'];
    const previousValues = this.pick(...changedValues);

    const options = {silent: true};
    const start = this.start();

    // Simplifies the logic so we don't have to handle null case.
    if (this.isEmpty()) {
      this.set('end', start, options);
      if (moveEnd === 'start') {
        this._swap();
      }
    }

    this.set('anchor', moveEnd === 'start' ? 'end' : 'start');
    this.set(moveEnd, pos, options);

    if (this.start() === this.end() && collapse) {
      this.set('end', null, options);
      this.setFreeEnd('end', options);
    }

    // Make sure start and end are properly set to 0 or length.
    this._cleanUpStartAndEnd(options);

    if (trigger) {
      const values = this.pick(...changedValues);
      this.set(previousValues, options);
      this.set(values);
    }

    this.unselectHelper(moveEnd);
    this._callHandles('linearExtend', oldPos, pos, moveEnd);
  }

  expand(x: number, trigger?: boolean): void {
    if (trigger == null) {
      trigger = true;
    }
    const length = this.getPolymerLength();
    if (length === 0) {
      return;
    }
    const start = this.start();
    const end = this.end()!;

    const allowCircular = (!this.isEmpty && sequtil.isWrap(start, end)) || !context.get('isLinear');
    const startDist = sequtil.distance(x, start, this.getPolymerLength(), allowCircular);
    const endDist = sequtil.distance(x, end, this.getPolymerLength(), allowCircular);

    const newFreeEnd = this.isEmpty()
      ? allowCircular
        ? // Use whichever results in a smaller selection
          sequtil.length(x, start, length) < sequtil.length(start, x, length)
          ? 'start'
          : 'end'
        : // Otherwise, use the end that results in valid, non-wrapping selection.
          x > start
          ? 'end'
          : 'start'
      : endDist < startDist
        ? 'end'
        : 'start';

    this.forceMoveEnd(newFreeEnd, x, trigger);
  }

  linearExtend(x: number, trigger?: boolean): void {
    if (trigger == null) {
      trigger = true;
    }
    const length = this.getPolymerLength();
    if (length === 0) {
      return;
    }
    // We have intermediate changes to selection that we don't want other people to know about,
    // so we change everything silently, then reset the old values, and set the new values without silent:true

    // Make the smallest change that will extend to x.
    const movedEnd = this.freeEnd();
    const oldX = this.get(this.freeEnd());
    const changedValues = ['start', 'end', 'anchor', 'startState', 'endState'];
    const previousValues = this.pick(...changedValues);
    const start = this.start();
    const end = this.end();
    const options = {silent: true};
    if (this.isEmpty()) {
      if (start !== x) {
        this.set('end', x, options);
        if (start > x) {
          this._swap(options);
        }
      }
    } else if (this.freeEnd() === 'end' && end! <= start && x >= start) {
      this.set('end', start, options);
    } else if (this.freeEnd() === 'start' && end! <= start && x <= end!) {
      this.set('start', end === length ? 0 : end!, options);
    } else {
      this.set(this.freeEnd(), x, options);
      if (
        (this.freeEnd() === 'start' && start < end! && x >= end!) ||
        (this.freeEnd() === 'end' && start < end! && x <= start)
      ) {
        this._swap(options);
      }

      if (this.start() === this.end()) {
        this.set('end', null, options);
        this.setFreeEnd('end', options);
      }
    }

    this._cleanUpStartAndEnd(options);

    if (trigger) {
      const values = this.pick(...changedValues);
      this.set(previousValues, options);
      this.set(values);
    }

    this.unselectHelper(movedEnd);
    this._callHandles('linearExtend', oldX, x, movedEnd);
  }

  private unselectHelper(movedEnd: Side): void {
    const stateGetterMethod = movedEnd === 'start' ? 'getStartState' : 'getEndState';
    const keys: Array<StartOrEndStateKey> = ['primer', 'cutsite'];
    keys
      .map((attribute) => this[stateGetterMethod](attribute))
      .forEach((maybeModel) => maybeModel?.set({selected: false}));
    this.set(movedEnd === 'start' ? 'startState' : 'endState', {});
  }

  linearExtendBy(delta: 1 | -1): void {
    assert([1, -1].includes(delta), '');

    // If the sequence is empty, give up.
    const length = this.getPolymerLength();
    if (length === 0) {
      return;
    }

    // We move by delta from the freeEnd (or in the empty case, wherever the cursor is)
    const oldX = (this.isEmpty() ? this.get('start') : this.get(this.freeEnd()))!;
    let newX = oldX + delta;

    // No wrapping on linear sequences.
    if ((newX < 0 || newX > length) && !this.isPolymerCircular()) {
      return;
    }

    // In situations where start becomes end, determine if the selection is about to
    // get bigger or smaller, to determine if selection should go to empty or full.
    const collapse =
      !this.isEmpty() &&
      ((delta > 0 && this.freeEnd() === 'start') || (delta < 0 && this.freeEnd() === 'end'));

    // If we have a full selection we cant extend the selection, it shouldn't do anything
    if (length === this.length() && !collapse) {
      return;
    }

    // Now that we handled the linear case, normalize the newX.
    if (newX !== length) {
      newX = sequtil.mod(newX, length);
    }

    if (this.isEmpty()) {
      if (delta < 0) {
        this.forceMoveEnd('start', newX, true, collapse);
      } else {
        this.forceMoveEnd('end', newX, true, collapse);
      }
    } else {
      this.forceMoveEnd(this.freeEnd(), newX, true, collapse);
    }
  }

  resetByRow(numRows: 1 | -1, rowWidth: number, columnState: number): void {
    const length = this.getPolymerLength();
    if (length === 0) {
      return;
    }
    let oldX = this.get(this.freeEnd());
    oldX = (oldX != null ? oldX : this.get(this.anchorEnd()))!;
    const totalRows = Math.ceil(length / rowWidth);
    const currentRow = Math.floor(oldX / rowWidth);
    let newRow = currentRow + numRows;

    if ((newRow < 0 || newRow >= totalRows) && !this.isPolymerCircular()) {
      return;
    }

    newRow %= totalRows;
    if (newRow < 0) {
      newRow += totalRows;
    }

    if (newRow === totalRows - 1) {
      this.reset(Math.min((totalRows - 1) * rowWidth + columnState, length));
    } else {
      this.reset(newRow * rowWidth + columnState);
    }
  }

  linearExtendByRow(numRows: 1 | -1, rowWidth: number, columnState: number): Selection | undefined {
    const length = this.getPolymerLength();
    if (length === 0) {
      return;
    }
    let oldX = this.get(this.freeEnd());
    oldX = (oldX != null ? oldX : this.get(this.anchorEnd()))!;
    const totalRows = Math.ceil(length / rowWidth);
    const currentRow = Math.floor(oldX / rowWidth);
    let newRow = currentRow + numRows;

    const changedValues = ['start', 'end', 'anchor', 'startState', 'endState'];
    const previousValues = this.pick(...changedValues);

    if ((newRow < 0 || newRow >= totalRows) && !this.isPolymerCircular()) {
      return;
    }

    newRow %= totalRows;
    const options = {silent: true};
    if (newRow < 0) {
      newRow += totalRows;
    }
    if (numRows > 0) {
      if (oldX === 0 && columnState !== 0) {
        this.linearExtend(columnState, false);
      } else if (newRow === totalRows - 1) {
        this.linearExtend(Math.min(length, rowWidth * numRows + oldX));
      } else if (currentRow + numRows >= totalRows) {
        this.linearExtend(length, false);
        if (this.length() !== length) {
          this.set(this.freeEnd(), 0, options);
          this.linearExtend(newRow * rowWidth + columnState, false);
        }
      } else {
        this.linearExtend(newRow * rowWidth + columnState, false);
      }
    } else if (numRows < 0) {
      if (oldX === 0) {
        this.set(this.freeEnd(), length, options);
        newRow -= 1;
      }

      if (newRow > currentRow) {
        this.linearExtend(0, false);
        const dist = newRow * rowWidth + columnState;
        if (dist < length && this.length() !== length) {
          this.set(this.freeEnd(), length, options);
          this.linearExtend(dist, false);
        }
      } else {
        this.linearExtend(newRow * rowWidth + columnState, false);
      }
    }

    this._cleanUpStartAndEnd(options);
    const values = this.pick(...changedValues);
    this.set(previousValues, options);
    return this.set(values);
  }

  _swap(options?: ModelSetOptions): void {
    if (options == null) {
      options = {};
    }
    // @ts-expect-error: this may not be safe
    this.set({start: this.end(), end: this.start()}, options);

    const startState = this.get('startState');
    const endState = this.get('endState');
    // Don't swap the primer state since it's strand-dependent.
    // eslint-disable-next-line no-restricted-syntax
    const newStartState = _.extend({}, startState, {cutsite: endState.cutsite});
    // eslint-disable-next-line no-restricted-syntax
    const newEndState = _.extend({}, endState, {cutsite: startState.cutsite});
    this.set({startState: newStartState, endState: newEndState}, options);

    this.setFreeEnd(this.anchorEnd(), options);
  }

  hasStartState(key: StartOrEndStateKey): boolean {
    // eslint-disable-next-line benchlint/no-in-operator
    return key in this.get('startState');
  }

  hasEndState(key: StartOrEndStateKey): boolean {
    // eslint-disable-next-line benchlint/no-in-operator
    return key in this.get('endState');
  }

  addStartState(toAdd: StartEndStateOptions): void {
    const state = this.get('startState');
    // eslint-disable-next-line no-var, vars-on-top, guard-for-in, no-restricted-syntax
    for (var key in toAdd) {
      const value = toAdd[key as StartOrEndStateKey]!;
      if (value.on != null) {
        // eslint-disable-next-line no-loop-func
        this.listenTo(value, 'destroy', function () {
          // @ts-expect-error: unbound `this`
          return this.unsetStartState(key);
        });
      }
    }
    // eslint-disable-next-line no-restricted-syntax
    this.set({startState: _.extend({}, state, toAdd)});
  }

  addEndState(toAdd: StartEndStateOptions): void {
    const state = this.get('endState');
    // eslint-disable-next-line no-var, vars-on-top, guard-for-in, no-restricted-syntax
    for (var key in toAdd) {
      const value = toAdd[key as StartOrEndStateKey]!;
      if (value.on != null) {
        // eslint-disable-next-line no-loop-func
        this.listenTo(value, 'destroy', function () {
          // @ts-expect-error: unbound `this`
          return this.unsetEndState(key);
        });
      }
    }
    // eslint-disable-next-line no-restricted-syntax
    this.set({endState: _.extend({}, state, toAdd)});
  }

  // It seems that this function was previously used more generically, but is
  // now just used to store `column` in a state object
  addState(toAdd: {column: number}): void {
    const state = this.get('state');
    // eslint-disable-next-line no-restricted-syntax
    this.set({state: _.extend({}, state, toAdd)});
  }

  unsetStartState(toDelete: StartOrEndStateKey): void {
    const state = this.get('startState');
    const deleted = state[toDelete];
    // eslint-disable-next-line @typescript-eslint/unbound-method
    if ((deleted != null ? deleted.off : undefined) != null) {
      this.stopListening(deleted);
    }
    // eslint-disable-next-line no-restricted-syntax
    this.set({startState: _.omit(state, toDelete)});
  }

  unsetEndState(toDelete: StartOrEndStateKey): void {
    const state = this.get('endState');
    const deleted = state[toDelete];
    // eslint-disable-next-line @typescript-eslint/unbound-method
    if ((deleted != null ? deleted.off : undefined) != null) {
      this.stopListening(deleted);
    }
    // eslint-disable-next-line no-restricted-syntax
    this.set({endState: _.omit(state, toDelete)});
  }

  unsetState(toDelete: 'column'): void {
    const state = this.get('state');
    // eslint-disable-next-line no-restricted-syntax
    this.set({state: _.omit(state, toDelete)});
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addHandlers(newHandlers: {[handler in Handler]?: (...args: Array<any>) => any}): Array<number> {
    const handlers = this.get('handlers');
    return (() => {
      // This is clearly not useful (it just ends up as a list [1...n] where n is the number of entries in `newHandlers`.
      // Leaving for now to minimize changes, but should be cleaned up.
      const result: Array<number> = [];
      // eslint-disable-next-line guard-for-in, no-restricted-syntax
      for (const key in newHandlers) {
        const value = newHandlers[key as Handler]!;
        if (handlers[key as Handler] == null) {
          handlers[key as Handler] = [];
        }
        result.push(handlers[key as Handler]!.push(value));
      }
      return result;
    })();
  }

  clearHandlers(): Selection {
    return this.set('handlers', {});
  }

  getState(key: 'column'): number | undefined {
    return this.get('state')[key];
  }

  getStartState<Key extends StartOrEndStateKey>(key: Key): StartEndStateOptions[Key] {
    return this.get('startState')[key];
  }

  getEndState<Key extends StartOrEndStateKey>(key: Key): StartEndStateOptions[Key] {
    return this.get('endState')[key];
  }

  storeEnzymes(
    clipboardMeta: ClipboardMeta,
    copyNucleotideSequenceMode?: CopyNucleotideSequenceMode
  ): [number, number] | undefined {
    if (this.canDigest()) {
      this.getStartState('cutsite')!.get('enzyme').set({active: true});
      this.getEndState('cutsite')!.get('enzyme').set({active: true});

      const enzymeIds: [number, number] = [
        this.getStartState('cutsite')!.get('enzyme').id,
        this.getEndState('cutsite')!.get('enzyme').id,
      ];
      if (
        copyNucleotideSequenceMode == null ||
        copyNucleotideSequenceMode === CopyNucleotideSequenceMode.DNA
      ) {
        // eslint-disable-next-line no-return-assign
        return (clipboardMeta['copied-enzymes'] = enzymeIds);
      }
    }
    // eslint-disable-next-line no-useless-return
    return;
  }

  invert(): Selection {
    let end;
    let start;
    for (const primer of [this.getStartState('primer'), this.getEndState('primer')]) {
      if (primer != null) {
        primer.set({selected: false});
      }
    }
    this.unsetStartState('primer');
    this.unsetEndState('primer');

    if (this.isPolymerCircular()) {
      if (this.isEmpty()) {
        this.setEndpoints(this.start(), this.start());
      } else if (this.length() === this.getPolymerLength()) {
        this.reset(this.start());
      } else {
        end = this.end() === this.getPolymerLength() ? 0 : this.end();
        start = this.start() === 0 ? this.getPolymerLength() : this.start();
        this.setEndpoints(end, start);
      }
    } else {
      assert(
        this.start() === 0 || this.end() === this.getPolymerLength(),
        `Shouldn't be inverting a selection (${this.start()}, ${this.end()}) on a linear sequence`
      );
      if (this.start() === 0 && this.end() === this.getPolymerLength()) {
        this.reset(0);
      } else if (this.start() === 0) {
        if (this.isEmpty()) {
          this.setEndpoints(0, this.getPolymerLength());
        } else {
          this.setEndpoints(this.end(), this.getPolymerLength());
        }
      } else {
        this.setEndpoints(0, this.start());
      }
    }
    return this.set({startState: this.get('endState'), endState: this.get('startState')});
  }

  clampToSequence(): void {
    const sequenceLength = this.getPolymerLength();
    const start = Math.min(sequenceLength, this.start());
    if (this.end() == null) {
      // If not a range-selection, just reset, if necessary.
      if (start !== this.start()) {
        this.reset(start);
      }
      return;
    }
    const end = Math.min(sequenceLength, this.end()!);
    if (start === this.start() && end === this.end()) {
      return;
    }
    if (start === end) {
      // If collapsed to single position, just reset instead.
      this.reset(start);
    } else {
      this.setEndpoints(start, end);
    }
  }

  clearState(): void {
    this.set({
      state: {},
      startState: {},
      endState: {},
    });
  }

  selectPair(primerFwd: SequencePrimer, primerRev: SequencePrimer): void {
    this.addStartState({primer: primerFwd});
    this.addEndState({primer: primerRev});
    this.setEndpoints(primerFwd.start(), primerRev.end());
    primerFwd.select();
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    return primerRev.select({multi: true});
  }
}

// "sequence" is actually not a defined attribute for the basic selection, but omitting it
// causes type issues since we extend from the base Selection class.
export interface BasicSelectionAttributes extends SelectionAttributes {
  polymerLength: number;
  circular: boolean;
}

interface BasicSelection {
  get<K extends keyof BasicSelectionAttributes>(key: K): BasicSelectionAttributes[K];
  set(obj: Partial<BasicSelectionAttributes>, options?: ModelSetOptions): BasicSelection;
  set<K extends keyof BasicSelectionAttributes>(
    key: K,
    value: BasicSelectionAttributes[K],
    options?: ModelSetOptions
  ): BasicSelection;
}

/**
 * A light-weight selection that does not rely on a sequence / polymer backbone model.
 */
class BasicSelection extends Selection {
  allowEdits(): boolean {
    return false;
  }

  getPolymerLength(): number {
    return this.get('polymerLength');
  }

  isPolymerCircular(): boolean {
    return this.get('circular');
  }

  deleteSelection(): never {
    throw new Error('Delete selection not supported');
  }

  isWritable(): boolean {
    return false;
  }

  isDeletable(): boolean {
    return false;
  }

  triggerJump(): void {}
}

export default {BasicSelection, Selection};
export {Selection, BasicSelection, NonEmptySelection};
